import React, { useEffect, useRef } from 'react';

// Shared dialog kit for the Cognitive Flows dashboard: a modal shell, a
// destructive-confirm dialog (the in-app replacement for window.confirm), and
// the button + focus-ring primitives the pages that open them share.
//
// Chrome follows CopyWorkflowToAccountModal (backdrop, centred dialog, header +
// close, right-aligned footer) retokenized onto global.css vars. Esc and a
// backdrop click both close, and focus moves into the dialog on open.
//
// global.css sets `button { outline: none }` app-wide, so focusRing restores a
// visible keyboard ring per element — the same inline-handler idiom the rest of
// the dashboard already uses for hover.

const FOCUS_COLOR = 'var(--cux-accent, #2563eb)';

// Spread onto any interactive element to get a keyboard-only focus ring back.
export const focusRing = {
  onFocus: (e) => {
    // :focus-visible keeps the ring off mouse clicks. Older engines get it always.
    try { if (!e.currentTarget.matches(':focus-visible')) return; } catch { /* no :focus-visible */ }
    e.currentTarget.style.outline = `2px solid ${FOCUS_COLOR}`;
    e.currentTarget.style.outlineOffset = '2px';
  },
  onBlur: (e) => { e.currentTarget.style.outline = 'none'; },
};

const BTN_BASE = {
  height: '36px', padding: '0 16px', borderRadius: 'var(--radius, 6px)',
  fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  transition: 'background 0.15s, border-color 0.15s',
};

const VARIANTS = {
  primary: { rest: 'var(--primary, var(--cux-ink, #132848))', hover: 'var(--primary-hover, var(--cux-ink-hover, #1a3560))', border: 'var(--primary, var(--cux-ink, #132848))', color: '#fff' },
  ghost: { rest: 'var(--cux-ghost-bg, #fff)', hover: '#f8fafc', border: 'var(--cux-ghost-border, var(--border, #e2e8f0))', color: 'var(--cux-ghost-text, var(--text-primary, #0f172a))' },
  danger: { rest: 'var(--error, #c81e1e)', hover: '#a91818', border: 'var(--error, #c81e1e)', color: '#fff' },
};

export function Button({ variant = 'ghost', disabled = false, style, children, ...rest }) {
  const tone = VARIANTS[variant] || VARIANTS.ghost;
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: tone.rest,
        border: `1px solid ${tone.border}`,
        color: tone.color,
        ...(disabled ? { opacity: 0.5, cursor: 'default' } : null),
        ...style,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = tone.hover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = tone.rest; }}
      {...focusRing}
      {...rest}
    >
      {children}
    </button>
  );
}

const s = {
  backdrop: {
    // Above the dashboard sidebar (#sidebar is 2000, its hover-expanded state
    // 2001 — App.css), so the scrim covers the whole app and the nav can't be
    // clicked through while a dialog is open.
    position: 'fixed', inset: 0, zIndex: 3000,
    background: 'rgba(15, 23, 42, 0.36)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
  },
  dialog: (width) => ({
    width: `min(${width}px, 100%)`, maxHeight: 'calc(100vh - 40px)',
    display: 'flex', flexDirection: 'column',
    background: 'var(--surface, #fff)',
    border: '1px solid var(--border, #e2e8f0)',
    borderRadius: 'var(--radius-lg, 12px)',
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
  }),
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '18px 20px 14px' },
  title: { margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-primary, #0f172a)', letterSpacing: '-0.01em' },
  close: {
    width: '30px', height: '30px', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid var(--border, #e2e8f0)', borderRadius: 'var(--radius, 6px)',
    background: '#fff', color: 'var(--text-secondary, #64748b)',
    fontSize: '15px', lineHeight: 1, cursor: 'pointer',
  },
  body: { padding: '0 20px 2px', overflowY: 'auto' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '18px 20px' },
};

let dialogSeq = 0;

export function Modal({ title, width = 520, busy = false, onClose, footer, children }) {
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const titleId = useRef(`cf-dialog-title-${++dialogSeq}`).current;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  useEffect(() => {
    // Move focus into the dialog so it is usable from the keyboard immediately.
    const t = setTimeout(() => {
      const first = bodyRef.current?.querySelector('input, select, textarea, button, [tabindex]:not([tabindex="-1"])');
      (first || dialogRef.current)?.focus();
    }, 30);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={s.backdrop} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div ref={dialogRef} tabIndex={-1} style={s.dialog(width)} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div style={s.head}>
          <h2 id={titleId} style={s.title}>{title}</h2>
          <button type="button" style={s.close} onClick={onClose} disabled={busy} aria-label="Close" {...focusRing}>✕</button>
        </div>
        <div ref={bodyRef} style={s.body}>{children}</div>
        {footer && <div style={s.foot}>{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busyLabel = 'Working…',
  danger = true,
  busy = false,
  onConfirm,
  onClose,
}) {
  return (
    <Modal
      title={title}
      width={440}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>{cancelLabel}</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
            {busy ? busyLabel : confirmLabel}
          </Button>
        </>
      )}
    >
      <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.65, color: 'var(--text-secondary, #64748b)' }}>{message}</p>
    </Modal>
  );
}
